import React from 'react';
import { useTranslation } from 'react-i18next';

import * as Styled from '../LoyaltyPageSeparated.styles';
import { ScreenType } from './LoyaltyTabs';

interface LoyaltyHintCardProps {
  screen: ScreenType;
}

interface HintStepConfig {
  titleKey?: string;
  textKey: string;
}

interface HintConfig {
  badgeType: 'letter' | 'check';
  badgeLetter?: string;
  badgeColor: string;
  titleKey: string;
  descKey: string;
  sectionLabelKey?: string;
  steps: HintStepConfig[];
  tipLabelKey: string;
  tipTextKey: string;
}

const CONFIG: Record<ScreenType, HintConfig> = {
  client: {
    badgeType: 'letter',
    badgeLetter: 'ПЛ',
    badgeColor: '#059669',
    titleKey: 'LoyaltySeparated.Hint.CLIENT_TITLE',
    descKey: 'LoyaltySeparated.Hint.CLIENT_DESC',
    sectionLabelKey: 'LoyaltySeparated.Hint.CLIENT_SECTION',
    steps: [
      { textKey: 'LoyaltySeparated.Hint.CLIENT_STEP_1' },
      { textKey: 'LoyaltySeparated.Hint.CLIENT_STEP_2' },
      { textKey: 'LoyaltySeparated.Hint.CLIENT_STEP_3' },
    ],
    tipLabelKey: 'LoyaltySeparated.Hint.CLIENT_TIP_LABEL',
    tipTextKey: 'LoyaltySeparated.Hint.CLIENT_TIP_TEXT',
  },
  receipt: {
    badgeType: 'check',
    badgeColor: '#059669',
    titleKey: 'LoyaltySeparated.Hint.RECEIPT_TITLE',
    descKey: 'LoyaltySeparated.Hint.RECEIPT_DESC',
    sectionLabelKey: 'LoyaltySeparated.Hint.RECEIPT_SECTION',
    steps: [
      { textKey: 'LoyaltySeparated.Hint.RECEIPT_STEP_1' },
      { textKey: 'LoyaltySeparated.Hint.RECEIPT_STEP_2' },
      { textKey: 'LoyaltySeparated.Hint.RECEIPT_STEP_3' },
    ],
    tipLabelKey: 'LoyaltySeparated.Hint.RECEIPT_TIP_LABEL',
    tipTextKey: 'LoyaltySeparated.Hint.RECEIPT_TIP_TEXT',
  },
  apply: {
    badgeType: 'letter',
    badgeLetter: 'Б',
    badgeColor: '#1a1f36',
    titleKey: 'LoyaltySeparated.Hint.APPLY_TITLE',
    descKey: 'LoyaltySeparated.Hint.APPLY_DESC',
    sectionLabelKey: 'LoyaltySeparated.Hint.APPLY_SECTION',
    steps: [
      {
        titleKey: 'LoyaltySeparated.Hint.APPLY_STEP_1_TITLE',
        textKey: 'LoyaltySeparated.Hint.APPLY_STEP_1_TEXT',
      },
    ],
    tipLabelKey: 'LoyaltySeparated.Hint.APPLY_TIP_LABEL',
    tipTextKey: 'LoyaltySeparated.Hint.APPLY_TIP_TEXT',
  },
  payment: {
    badgeType: 'letter',
    badgeLetter: 'Т',
    badgeColor: '#6a3df5',
    titleKey: 'LoyaltySeparated.Hint.PAYMENT_TITLE',
    descKey: 'LoyaltySeparated.Hint.PAYMENT_DESC',
    sectionLabelKey: 'LoyaltySeparated.Hint.PAYMENT_SECTION',
    steps: [
      {
        titleKey: 'LoyaltySeparated.Hint.PAYMENT_STEP_1_TITLE',
        textKey: 'LoyaltySeparated.Hint.PAYMENT_STEP_1_TEXT',
      },
      {
        titleKey: 'LoyaltySeparated.Hint.PAYMENT_STEP_2_TITLE',
        textKey: 'LoyaltySeparated.Hint.PAYMENT_STEP_2_TEXT',
      },
    ],
    tipLabelKey: 'LoyaltySeparated.Hint.PAYMENT_TIP_LABEL',
    tipTextKey: 'LoyaltySeparated.Hint.PAYMENT_TIP_TEXT',
  },
  processing: {
    badgeType: 'check',
    badgeColor: '#059669',
    titleKey: 'LoyaltySeparated.Hint.PROCESSING_TITLE',
    descKey: 'LoyaltySeparated.Hint.PROCESSING_DESC',
    steps: [
      {
        titleKey: 'LoyaltySeparated.Hint.PROCESSING_STEP_1_TITLE',
        textKey: 'LoyaltySeparated.Hint.PROCESSING_STEP_1_TEXT',
      },
      {
        titleKey: 'LoyaltySeparated.Hint.PROCESSING_STEP_2_TITLE',
        textKey: 'LoyaltySeparated.Hint.PROCESSING_STEP_2_TEXT',
      },
      {
        titleKey: 'LoyaltySeparated.Hint.PROCESSING_STEP_3_TITLE',
        textKey: 'LoyaltySeparated.Hint.PROCESSING_STEP_3_TEXT',
      },
    ],
    tipLabelKey: 'LoyaltySeparated.Hint.PROCESSING_TIP_LABEL',
    tipTextKey: 'LoyaltySeparated.Hint.PROCESSING_TIP_TEXT',
  },
};

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path
      d="M2.5 7.5L5.5 10.5L11.5 3.5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const LoyaltyHintCard: React.FC<LoyaltyHintCardProps> = ({ screen }) => {
  const { t } = useTranslation();
  const config = CONFIG[screen];

  if (!config) return null;

  return (
    <Styled.HintCard>
      <Styled.HintBadgeRow>
        {config.badgeType === 'check' ? (
          <Styled.HintCheckIcon>
            <CheckIcon />
          </Styled.HintCheckIcon>
        ) : (
          <Styled.HintBadgeIcon bg={config.badgeColor}>
            {config.badgeLetter}
          </Styled.HintBadgeIcon>
        )}
        <Styled.HintBadgeLabel>
          {t('LoyaltySeparated.Hint.BADGE')}
        </Styled.HintBadgeLabel>
      </Styled.HintBadgeRow>

      <Styled.HintTitle>{t(config.titleKey)}</Styled.HintTitle>
      <Styled.HintDescription>{t(config.descKey)}</Styled.HintDescription>

      {config.sectionLabelKey && (
        <Styled.HintSectionLabel>
          {t(config.sectionLabelKey)}
        </Styled.HintSectionLabel>
      )}

      <Styled.HintStepsList>
        {config.steps.map((step, index) => (
          <Styled.HintStep key={step.textKey}>
            <Styled.HintStepNumber>{index + 1}</Styled.HintStepNumber>
            <Styled.HintStepText>
              {step.titleKey && <strong>{t(step.titleKey)}</strong>}
              {t(step.textKey)}
            </Styled.HintStepText>
          </Styled.HintStep>
        ))}
      </Styled.HintStepsList>

      <Styled.HintTipBox>
        <Styled.HintTipLabel>{t(config.tipLabelKey)}</Styled.HintTipLabel>
        <Styled.HintTipText>{t(config.tipTextKey)}</Styled.HintTipText>
      </Styled.HintTipBox>
    </Styled.HintCard>
  );
};

export default LoyaltyHintCard;
